/*
 * Relax Detail film scrub.
 * Draws a numbered AVIF frame sequence to a canvas while a pinned section scrolls.
 * Needs gsap + ScrollTrigger (and Lenis, optional) loaded first.
 *
 * Markup contract:
 *   <section id="film">            pinned element, full viewport
 *     <canvas class="rs-canvas">   the film
 *     <div data-chapter data-from="10" data-to="36" data-tone="light">  overlay text, one per chapter
 *     <div data-rs-progress>       optional: gets --rs-p (0 to 1) for a progress line
 *     <div data-rs-counter>        optional: text set to the current frame number
 *   </section>
 *
 * Classes it sets:
 *   chapter.is-active   while the current frame is inside the chapter's range
 *   chapter --cp        local progress inside the chapter, 0 to 1
 *   section[data-tone]  the active chapter's tone
 *   section.rs-static   reduced motion: no pin, last frame shown, every chapter active
 *   html.rs-ready       first frame drawn
 *
 * Options worth knowing: blend (cross-fade neighbouring frames so a short sequence plays
 * like video) and scrub (ScrollTrigger smoothing in seconds; true means none).
 */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var touch = window.matchMedia('(hover: none), (pointer: coarse)').matches;

  function pad(n) { return String(n).padStart(4, '0'); }

  function startLenis() {
    if (reduce || touch || !window.Lenis || !window.gsap) return null;
    var lenis = new window.Lenis({ lerp: 0.1, wheelMultiplier: 1 });
    lenis.on('scroll', window.ScrollTrigger.update);
    window.gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
    window.gsap.ticker.lagSmoothing(0);
    window.relaxLenis = lenis;
    return lenis;
  }

  function init(opts) {
    var section = typeof opts.section === 'string' ? document.querySelector(opts.section) : opts.section;
    if (!section) return;
    var canvas = section.querySelector('.rs-canvas');
    var ctx = canvas.getContext('2d');
    var total = opts.frames || 197;
    var path = opts.path || function (i) { return 'frames/f_' + pad(i) + '.avif'; };
    var length = opts.length || 6.2;          // scroll length in viewport heights
    var hold = opts.hold || 0;                // extra viewport heights on the last frame at the end
    var focal = opts.focal || [[1, 0.5]];      // [fromFrame, focalX] pairs for narrow screens
    var invertUntil = opts.invertUntil || 0;   // frames 1..n drawn inverted, fading out over 4 frames
    var chapters = Array.prototype.slice.call(section.querySelectorAll('[data-chapter]'));
    var progressEl = section.querySelector('[data-rs-progress]');
    var counterEl = section.querySelector('[data-rs-counter]');
    var images = new Array(total + 1);
    var loaded = new Array(total + 1);
    var current = 0, currentT = -1;
    var w = 0, h = 0;
    var blend = !!opts.blend;

    function focalFor(i) {
      var x = 0.5;
      for (var k = 0; k < focal.length; k++) if (i >= focal[k][0]) x = focal[k][1];
      return x;
    }

    function size() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var r = canvas.getBoundingClientRect();
      w = Math.round(r.width * dpr);
      h = Math.round(r.height * dpr);
      canvas.width = w;
      canvas.height = h;
      draw(current || 1, true);
    }

    function nearest(i) {
      if (loaded[i]) return i;
      for (var d = 1; d < total; d++) {
        if (i - d >= 1 && loaded[i - d]) return i - d;
        if (i + d <= total && loaded[i + d]) return i + d;
      }
      return 0;
    }

    function paint(n, alpha) {
      var img = images[n];
      var iw = img.naturalWidth, ih = img.naturalHeight;
      var scale = Math.max(w / iw, h / ih);
      var dw = iw * scale, dh = ih * scale;
      var dx = (w - dw) * focalFor(n);
      var dy = (h - dh) / 2;
      ctx.globalAlpha = alpha;
      ctx.filter = 'none';
      if (invertUntil && n <= invertUntil + 4) {
        var amt = n <= invertUntil ? 1 : 1 - (n - invertUntil) / 4;
        ctx.filter = 'invert(' + amt + ')';
      }
      ctx.drawImage(img, dx, dy, dw, dh);
      ctx.filter = 'none';
      ctx.globalAlpha = 1;
    }

    // i may be fractional when blending: the next frame is laid over at that strength.
    function draw(i, force) {
      var a = Math.floor(i), t = blend ? Math.round((i - a) * 20) / 20 : 0;
      var n = nearest(a);
      if (!n) return;
      var b = Math.min(total, n + 1);
      if (!t || !loaded[b] || n !== a) t = 0;
      if (!force && n === current && t === currentT) return;
      current = n; currentT = t;
      paint(n, 1);
      if (t) paint(b, t);
    }

    function load(i) {
      if (images[i]) return Promise.resolve();
      return new Promise(function (res) {
        var img = new Image();
        img.decoding = 'async';
        img.onload = function () { loaded[i] = true; res(); };
        img.onerror = function () { res(); };
        img.src = path(i);
        images[i] = img;
      });
    }

    // Load the first frame, then coarse to fine so scrubbing works before everything arrives.
    function loadAll() {
      var order = [];
      var seen = {};
      [16, 8, 4, 2, 1].forEach(function (step) {
        for (var i = 1; i <= total; i += step) if (!seen[i]) { seen[i] = 1; order.push(i); }
      });
      if (!seen[total]) order.push(total);
      var idx = 0;
      function next() {
        if (idx >= order.length) return;
        var batch = order.slice(idx, idx + 6);
        idx += 6;
        Promise.all(batch.map(load)).then(function () { draw(blend ? 1 + lastP * (total - 1) : frameFor(lastP), true); next(); });
      }
      next();
    }

    var lastP = 0;
    function frameFor(p) { return Math.min(total, Math.max(1, Math.round(1 + p * (total - 1)))); }

    function update(p) {
      p = hold ? Math.min(1, p * (length + hold) / length) : p;
      lastP = p;
      var f = frameFor(p);
      draw(blend ? 1 + p * (total - 1) : f);
      if (progressEl) progressEl.style.setProperty('--rs-p', p.toFixed(4));
      if (counterEl) counterEl.textContent = pad(f).slice(1);
      var tone = null;
      chapters.forEach(function (c) {
        var from = +c.dataset.from, to = +c.dataset.to;
        var on = f >= from && f <= to;
        c.classList.toggle('is-active', on);
        if (on) {
          c.style.setProperty('--cp', ((f - from) / Math.max(1, to - from)).toFixed(3));
          tone = c.dataset.tone || tone;
        }
      });
      if (tone) section.dataset.tone = tone;
      if (opts.onFrame) opts.onFrame(f, p);
    }

    // Frames download only once the visitor starts to scroll or interact, so the first view costs
    // one poster image (the Elementor image under the canvas), not the whole film.
    var begun = false;
    var kick = ['scroll', 'wheel', 'touchstart', 'pointerdown', 'keydown'];
    function begin() {
      if (begun) return;
      begun = true;
      kick.forEach(function (ev) { window.removeEventListener(ev, begin, { passive: true }); });
      firstFrame();
    }
    if (reduce) { begin(); } else { kick.forEach(function (ev) { window.addEventListener(ev, begin, { passive: true }); }); }

    function firstFrame() { load(1).then(function () {
      size();
      document.documentElement.classList.add('rs-ready');
      if (reduce) {
        section.classList.add('rs-static');
        load(opts.staticFrame || 195).then(function () { draw(opts.staticFrame || 195, true); });
        chapters.forEach(function (c) { c.classList.add('is-active'); });
        return;
      }
      loadAll();
    }); }
    window.addEventListener('resize', size);

    if (reduce || !window.gsap) return;
    window.gsap.registerPlugin(window.ScrollTrigger);
    window.ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: function () { return '+=' + Math.round(window.innerHeight * (length + hold)); },
      anticipatePin: 1,
      pin: true,
      scrub: opts.scrub === undefined ? true : opts.scrub,
      onUpdate: function (st) { update(st.progress); },
      onRefresh: function (st) { update(st.progress); }
    });
    update(0);
  }

  window.RelaxScrub = { init: init, startLenis: startLenis, reduce: reduce, touch: touch };
})();
